import { apiClient, type Paginated } from '@/core/api';
import type { Lead } from '../leads/types';
import type { Bid, BidInput, BidRollup } from './types';

export const BASE = '/crm/bids/';

export interface BidListParams {
  page?: number;
  search?: string;
  channel?: string;
  status?: string;
  origin?: string;
  owner?: number;
  mine?: boolean;
  boosted?: boolean;
  bid_vs_median?: string;
  rate_band?: string;
  client_country?: string;
  client_hire_rate_min?: number;
  converted?: boolean;
  /** `true` → only archived rows; `'all'` → both. Unset/`false` → active only. */
  archived?: boolean | 'all';
  ordering?: string;
  /** Submitted window — ISO bounds; `_before` should be end-of-day. */
  submitted_after?: string;
  submitted_before?: string;
}

export interface BidRollupParams extends Omit<BidListParams, 'page' | 'ordering'> {
  /** Comma-separated dimensions, e.g. `week,channel`. */
  group_by?: string;
}

export async function listBids(params: BidListParams = {}): Promise<Paginated<Bid>> {
  const { data } = await apiClient.get<Paginated<Bid>>(BASE, { params });
  return data;
}

export async function getBid(id: string | number): Promise<Bid> {
  const { data } = await apiClient.get<Bid>(`${BASE}${id}/`);
  return data;
}

export async function createBid(input: BidInput): Promise<Bid> {
  const { data } = await apiClient.post<Bid>(BASE, input);
  return data;
}

export async function updateBid(id: string | number, input: Partial<BidInput>): Promise<Bid> {
  const { data } = await apiClient.patch<Bid>(`${BASE}${id}/`, input);
  return data;
}

export async function archiveBid(id: string | number): Promise<void> {
  await apiClient.delete(`${BASE}${id}/`);
}

export async function restoreBid(id: string | number): Promise<Bid> {
  const { data } = await apiClient.post<Bid>(`${BASE}${id}/restore/`);
  return data;
}

export async function purgeBid(id: string | number): Promise<void> {
  await apiClient.delete(`${BASE}${id}/purge/`);
}

/** Convert this bid to a Lead — once (server-side, audited). Returns the Lead. */
export async function convertBid(id: string | number): Promise<Lead> {
  const { data } = await apiClient.post<Lead>(`${BASE}${id}/convert/`, {});
  return data;
}

/** Weekly roll-up over the full filtered set (MEM-0002: never sum a page). */
export async function getBidRollup(params: BidRollupParams = {}): Promise<BidRollup> {
  const { data } = await apiClient.get<BidRollup>(`${BASE}summary/`, { params });
  return data;
}
